import { P } from 'components'
import CustomButton from 'components/Buttons/CustomButton'
import { useReleaseBySellerMutation } from 'features/public-views/order/requestTransactionApiSlice'
import usePartialUpload from 'hooks/usePartialUpload'
import { useSnackbar } from 'notistack'
import React, { useRef, useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import Webcam from 'react-webcam'
import { Modal, ModalBody, Spinner, Input } from 'reactstrap'

const ModalSellerConfirmTrx = ({
  showConfirmTransactionBySeller,
  toggle,
  transaction,
  handleRefetch,
  refetchTransactionById,
  refetchTransactionAdjustment,
}) => {
  const webcamRef = useRef(null)
  const dispatch = useDispatch()
  const { uploadFile } = usePartialUpload()
  const { enqueueSnackbar } = useSnackbar()

  const [image, setImage] = useState(null)
  const [imageUrl, setImageUrl] = useState(null)
  const [isUploading, setIsUploading] = useState(false)
  const [note, setNote] = useState('')

  const [releaseBySeller, { isLoading: isLoadingRelease }] =
    useReleaseBySellerMutation()

  const handleCaptureAndUpload = async () => {
    const imageSrc = webcamRef.current.getScreenshot()

    if (!imageSrc) return

    setImage(imageSrc)

    const blob = await fetch(imageSrc).then((res) => res.blob())
    const file = new File([blob], 'confirmation.jpg', { type: 'image/jpeg' })

    try {
      setIsUploading(true)
      const result = await uploadFile(
        file,
        () => {},
        'USER_ID',
        'SELLERCONFIRMTRX',
        true,
      )
      if (result?.url) {
        setImageUrl(result.url)
      } else {
        enqueueSnackbar(result?.message || 'Upload gagal. Coba lagi.', { variant: 'error' })
        setImage(null)
      }
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message || error?.message || 'Upload error. Coba lagi.',
        { variant: 'error' }
      )
      setImage(null)
    } finally {
      setIsUploading(false)
    }
  }

  const handleRetake = () => {
    setImage(null)
    setImageUrl(null)
  }

  const handleReleaseBySeller = async () => {
    try {
      const payload = {
        ...((transaction?.orderDetail?.useProofOfPayment ||
          transaction?.orderDetail?.useProofOfpayment) &&
          transaction?.orderDetail?.paymentTime && {
            proofOfPayment: imageUrl,
          }),
        escrowpaymentId: transaction?.escrowpaymentId,
        linkselfie: imageUrl,
        note: note,
      }
      await releaseBySeller(payload).unwrap()
      handleRefetch?.()
      try {
        refetchTransactionById?.()
      } catch (e) {
        console.error(e)
      }
      try {
        refetchTransactionAdjustment?.()
      } catch (e) {
        // Query might be skipped
      }
      enqueueSnackbar('Konfirmasi berhasil!', { variant: 'success' })
    } catch (error) {
      enqueueSnackbar(error?.data?.message || 'Terjadi kesalahan.', {
        variant: 'error',
      })
    } finally {
      toggle()
    }
  }

  useEffect(() => {
    if (!showConfirmTransactionBySeller) {
      setImage(null)
      setImageUrl(null)
      setIsUploading(false)
      setNote('')
    }
  }, [showConfirmTransactionBySeller])

  return (
    <Modal
      className="p-2"
      centered
      isOpen={showConfirmTransactionBySeller}
      toggle={toggle}
      autoFocus={false}
      size="md"
      data-testid="detail-transaction-modal-confirm"
    >
      <ModalBody className="p-4">
        <div className="text-center mb-4">
          <P size="large" className="fw-bold mb-2" color="#101828">
            Konfirmasi pesanan
          </P>
          <P size="regular" className="text-muted">
            Silakan ambil foto sebagai bukti sebelum Anda mengonfirmasi transaksi.
          </P>
        </div>

        <div className="position-relative mb-4 overflow-hidden rounded-lg shadow-sm border" style={{ aspectRatio: '4/3', backgroundColor: '#f9fafb' }}>
          {image ? (
            <img
              src={image}
              alt="Captured"
              className="w-100 h-100"
              style={{ objectFit: 'cover' }}
            />
          ) : (
            <Webcam
              ref={webcamRef}
              audio={false}
              screenshotFormat="image/jpeg"
              className="w-100 h-100"
              style={{ objectFit: 'cover' }}
            />
          )}
          
          {isUploading && (
            <div className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center" style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', zIndex: 10 }}>
              <Spinner color="primary" className="mb-2" />
              <P size="small" className="fw-medium text-primary">Mengunggah...</P>
            </div>
          )}
        </div>

        <div className="mb-4">
          {image ? (
            <CustomButton
              onClick={handleRetake}
              color="secondary"
              outline
              block
              disabled={isUploading}
              className="py-2 fw-semibold"
            >
              Ulang Foto
            </CustomButton>
          ) : (
            <CustomButton
              onClick={handleCaptureAndUpload}
              color="primary"
              block
              disabled={isUploading}
              className="py-2 fw-semibold"
            >
              Ambil Foto
            </CustomButton>
          )}
        </div>

        <div className="mb-4 text-start">
          <P size="regular" className="fw-semibold mb-1" color="#101828">
            Catatan (Opsional)
          </P>
          <Input
            type="textarea"
            rows={3}
            placeholder="Tambahkan catatan jika ada"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>

        <div className="d-flex gap-3">
          <CustomButton 
            onClick={toggle} 
            className="btn-cancel py-2 fw-semibold border-0" 
            style={{ backgroundColor: '#F2F4F7', color: '#344054' }}
            block
            testId="detail-transaction-modal-confirm-cancel"
          >
            Batal
          </CustomButton>
          <CustomButton
            color="primary"
            disabled={!imageUrl || isLoadingRelease || isUploading}
            block
            onClick={handleReleaseBySeller}
            className="py-2 fw-semibold"
            testId="detail-transaction-button-confirm-trx"
          >
            {isLoadingRelease ? <Spinner size="sm" /> : 'Konfirmasi'}
          </CustomButton>
        </div>
      </ModalBody>
    </Modal>
  )
}

export default ModalSellerConfirmTrx

